import React, {
    useRef, useState, useEffect, useCallback,
    forwardRef,
    cloneElement
} from 'react'
import { useTheme } from 'contexts/theme'
import { v4 } from 'uuid'
import debounce from 'lodash.debounce'
import { getUserAgent } from 'utilities/user-agent'
import { cls } from 'utilities/dom'
import { omit } from 'utilities/object'
import FallbackPopover from './fallback'
import {
    PopoverContent, PopoverTrigger,
    TooltipTriggerWrapper, TooltipTriggerIcon
} from './s'

const Popover = ({
    content, children,
    placement = 'top',
    delay = 0, // Delay in ms, either a number or an array of two numbers [show, hide]
    duration = 100, // Animation (in) duration in ms
    distance = 8, // Natural distance from trigger in px
    offset = [0, 0], // Offset (x, y) in px
    trigger = 'hover', hideOnContentClick = false, renderContent = true, show = false,
    callback,
    className,
    ...props
}) => {
    const popoverRef = useRef()
    const triggerRef = useRef()

    const { activeClickPopovers } = useTheme()

    const [showDelay, hideDelay] = Array.isArray(delay) ? delay : [delay, delay]
    const [showing, setShowing] = useState(show)
    const [id] = useState(v4())

    useEffect(() => {
        if(!popoverRef.current) {
            return
        }

        popoverRef.current.addEventListener('beforetoggle', ({ newState }) => {
            setShowing(newState === 'open')

            if(trigger === 'click') {
                if(newState === 'open') {
                    activeClickPopovers.add(id)
                } else {
                    activeClickPopovers.delete(id)
                }
            }

            if(callback) {
                callback({ open: newState === 'open' })
            }
        })
    }, [callback])

    useEffect(() => {
        if(popoverRef.current) {
            if(!show) {
                popoverRef.current.hidePopover()
            }
        }
    }, [show, popoverRef])

    const handleTriggerOn = useCallback(() => {
        const showIfHovered = () => {
            if(trigger !== 'click' && !!activeClickPopovers.size) {
                return
            }

            if(triggerRef.current?.matches(':hover')) {
                popoverRef.current.showPopover()
            }
        }

        debounce(showIfHovered, showDelay)()
    }, [showDelay])

    const handleTriggerOff = useCallback(event => {
        debounce(() => {
            // For instance, when the mouse leaves the viewport, then hide the popover
            if(!(event.relatedTarget instanceof Node)) {
                popoverRef.current.hidePopover()
                return
            }

            // Make sure the popover is not hidden when the mouse moves from the trigger to the popover, or to a child of the popover
            if(popoverRef.current?.contains(event.relatedTarget)) {
                return
            }

            popoverRef.current.hidePopover()
        }, hideDelay)()
    }, [hideDelay])

    const triggerProps = {
        ...(trigger === 'hover') && {
            onMouseEnter: handleTriggerOn,
            onMouseLeave: handleTriggerOff
        },
        ...(trigger === 'click') && {
            role: 'button',
            popovertarget: id
        },
        style: {
            ...children.props?.style,
            anchorName: `--${id}`
        },
        ref: triggerRef
    }

    const isVertical = /top|bottom/.test(placement)

    const horizontalDistanceOffset = (isVertical ? 0 : distance) + offset[0]
    const verticalDistanceOffset = (isVertical ? distance : 0) + offset[1]

    return (
        <>
            {renderContent && (
                <PopoverContent
                    {...props}
                    id={id}
                    popover="auto"
                    style={{
                        '--__huma-component-popover-distance-x': `${horizontalDistanceOffset}px`,
                        '--__huma-component-popover-distance-y': `${verticalDistanceOffset}px`,
                        '--__huma-component-popover-duration': `${duration}ms`,
                        positionAnchor: `--${id}`
                    }}
                    className={cls(className, placement)}
                    ref={popoverRef}>
                    {((typeof content === 'string' || Array.isArray(content)) && !!showing) && (
                        <span>{content}</span>
                    )}
                    {(typeof content !== 'string' && !Array.isArray(content) && !!showing) && (
                        cloneElement(content, {
                            ...(hideOnContentClick ? {
                                onClickCapture: () => requestAnimationFrame(() => popoverRef.current?.hidePopover?.())
                            } : null)
                        })
                    )}
                </PopoverContent>
            )}
            {(typeof children === 'string') && (
                <PopoverTrigger {...triggerProps}>
                    {children}
                </PopoverTrigger>
            )}
            {(typeof children !== 'string') && (
                cloneElement(children, triggerProps)
            )}
        </>
    )
}

export const Tooltip = ({ className, trigger = 'hover', ...props }) => {
    const { supportsAnchorPositioning } = useTheme()
    const { isMobile } = getUserAgent()

    if(!!supportsAnchorPositioning) {
        return (
            <Popover
                className={cls('tooltip', className)}
                trigger={trigger}
                renderContent={(trigger !== 'click') && !isMobile}
                {...props} />
        )
    }

    return (
        <FallbackPopover
            className={cls('tooltip', className)}
            trigger="mouseenter"
            {...props} />
    )
}

export const TooltipTrigger = forwardRef(({ lineHeight: $lineHeight, size = 16, icon: Icon = TooltipTriggerIcon, ...props }, ref) => (
    <TooltipTriggerWrapper
        {...props}
        $lineHeight={$lineHeight}
        ref={ref}>
        {!!Icon && <Icon size={size} />}
    </TooltipTriggerWrapper>
))

export default props => {
    const { supportsAnchorPositioning } = useTheme()

    if(!!supportsAnchorPositioning) {
        props = omit(props, 'interactive')

        return <Popover {...props} />
    }

    return <FallbackPopover {...props} />
}